<template>
  <div class="p-6 bg-white rounded-lg shadow-md">
    <h1 class="text-3xl font-bold mb-6 text-gray-800">Gestion des Badges</h1>

    <div class="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
      <label for="badgeCompany" class="block text-sm font-medium text-gray-700 mb-2">Société</label>
      <select
        id="badgeCompany"
        v-model="selectedCompanyId"
        class="w-full md:w-96 border border-gray-300 rounded-md shadow-sm p-2 bg-white"
      >
        <option v-for="company in companies" :key="company.id" :value="company.id">
          {{ company.name }}
        </option>
      </select>
    </div>

    <div class="mb-4 flex gap-2">
      <input
        type="text"
        v-model="newBadgeNumber"
        placeholder="Nouveau badge"
        class="flex-1 border border-gray-300 rounded-md shadow-sm p-2"
      />
      <button
        @click="addBadge"
        class="px-4 py-2 bg-orange-600 text-white font-semibold rounded-md shadow-sm hover:bg-orange-700"
      >
        Ajouter
      </button>
    </div>

    <ul class="border border-gray-200 rounded-md divide-y divide-gray-200 bg-white">
      <li v-for="badge in badges" :key="badge.id" class="px-4 py-3 flex justify-between items-center">
        <span>{{ badge.badge_number }}</span>
        <div class="space-x-3">
          <button @click="renameBadge(badge)" class="text-blue-600 hover:text-blue-900">Renommer</button>
          <button @click="deleteBadge(badge.id)" class="text-red-600 hover:text-red-900">Supprimer</button>
        </div>
      </li>
      <li v-if="badges.length === 0" class="px-4 py-3 text-gray-500">Aucun badge.</li>
    </ul>

    <p v-if="errorMessage" class="text-red-600 text-sm mt-4">{{ errorMessage }}</p>
    <p v-if="successMessage" class="text-green-600 text-sm mt-4">{{ successMessage }}</p>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import apiFetch, { companyApi } from '@/apiClient';

export default {
  name: 'BadgesManagementView',
  setup() {
    const companies = ref([]);
    const selectedCompanyId = ref(null);
    const badges = ref([]);
    const newBadgeNumber = ref('');
    const errorMessage = ref('');
    const successMessage = ref('');

    const selectedCompany = () => companies.value.find((company) => company.id === selectedCompanyId.value);

    const fetchCompanies = async () => {
      const data = await apiFetch('/companies');
      companies.value = data;

      const administration = companies.value.find((company) => company.name === 'Administration');
      selectedCompanyId.value = administration ? administration.id : (companies.value[0]?.id || null);
    };

    const fetchBadges = async () => {
      if (!selectedCompanyId.value) {
        badges.value = [];
        return;
      }
      badges.value = await companyApi.getBadges(selectedCompanyId.value);
    };

    const addBadge = async () => {
      const badgeNumber = newBadgeNumber.value.trim();
      if (!selectedCompanyId.value || !badgeNumber) return;

      await companyApi.createBadge(selectedCompanyId.value, { badge_number: badgeNumber });
      successMessage.value = `Badge ajouté pour ${selectedCompany()?.name || 'la société'}.`;
      errorMessage.value = '';
      newBadgeNumber.value = '';
      await fetchBadges();
    };

    const renameBadge = async (badge) => {
      const number = prompt('Nouveau numéro de badge', badge.badge_number);
      if (!number || !number.trim()) return;

      await companyApi.updateBadge(selectedCompanyId.value, badge.id, { badge_number: number.trim() });
      successMessage.value = 'Badge mis à jour.';
      errorMessage.value = '';
      await fetchBadges();
    };

    const deleteBadge = async (badgeId) => {
      if (!confirm('Supprimer ce badge ?')) return;

      await companyApi.deleteBadge(selectedCompanyId.value, badgeId);
      successMessage.value = 'Badge supprimé.';
      errorMessage.value = '';
      await fetchBadges();
    };

    onMounted(async () => {
      errorMessage.value = '';
      successMessage.value = '';
      try {
        await fetchCompanies();
        await fetchBadges();
      } catch (error) {
        errorMessage.value = error.message;
      }
    });

    watch(selectedCompanyId, async () => {
      errorMessage.value = '';
      successMessage.value = '';
      try {
        await fetchBadges();
      } catch (error) {
        errorMessage.value = error.message;
      }
    });

    return {
      companies,
      selectedCompanyId,
      badges,
      newBadgeNumber,
      errorMessage,
      successMessage,
      addBadge,
      renameBadge,
      deleteBadge,
    };
  },
};
</script>
